import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Typography,
  message,
  DatePicker,
  Modal,
} from 'antd';
import {
  UserOutlined,
  CarOutlined,
  EditOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { Estimate, EstimateLineItem, Customer, Vehicle } from '../types';
import CustomerInlineForm from './CustomerInlineForm';
import VehicleInlineForm from './VehicleInlineForm';
import VehicleForm from './VehicleForm';
import PartsCatalogButton from './PartsCatalogButton';
import { estimateApi, shopSettingsApi, customerApi, vehicleApi } from '../services/api';
import dayjs from 'dayjs';
import { useDebounce } from '../hooks';
import { useIsMobile } from '../hooks/useIsMobile';
import PageRefreshWarning from './PageRefreshWarning';
import { EstimateFormProvider, useEstimateFormContext } from '../contexts/EstimateFormContext';
import UnifiedAIChat from './UnifiedAIChat';
import { logEvent, newEventId } from '../services/eventLogger';
import { calculateEstimateTotals } from '../utils/estimateTotals';
import { EstimateHeader, EstimateFormFields, EstimateLineItems, useEstimateAI } from './Estimates';

const { Text } = Typography;

const EstimateForm: React.FC = () => (
  <EstimateFormProvider>
    <EstimateFormInner />
  </EstimateFormProvider>
);

const EstimateFormInner: React.FC = () => <DesktopEstimateForm />;

const DesktopEstimateForm: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const isMobile = useIsMobile(768);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [lineItems, setLineItems] = useState<EstimateLineItem[]>([]);
  const [taxRate, setTaxRate] = useState(7);
  const [taxRateInput, setTaxRateInput] = useState('7');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [draftEstimateId, setDraftEstimateId] = useState<number | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [editCustomerModalOpen, setEditCustomerModalOpen] = useState(false);
  const [editVehicleModalOpen, setEditVehicleModalOpen] = useState(false);
  const [editCustomerForm] = Form.useForm();
  const [defaultLaborRate, setDefaultLaborRate] = useState<number>(175);

  useEffect(() => {
    shopSettingsApi.get()
      .then((res) => { if (res.data?.labor_rate) setDefaultLaborRate(res.data.labor_rate); })
      .catch(() => {});
  }, []);

  const formContext = useEstimateFormContext();
  const { aiAnalysis } = formContext;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const debouncedTaxRate = useDebounce(taxRate, 300);

  // AI hook — all AI-related callbacks and state
  const ai = useEstimateAI({
    form, formContext, estimate, lineItems, setLineItems,
    selectedCustomer, setSelectedCustomer, selectedVehicle, setSelectedVehicle,
    setHasUnsavedChanges, defaultLaborRate,
  });

  useEffect(() => {
    if (isEditing && id) fetchEstimate(parseInt(id));
  }, [isEditing, id]);

  const fetchEstimate = async (estimateId: number) => {
    try {
      setLoading(true);
      const response = await estimateApi.getById(estimateId);
      const found = response.data;
      setEstimate(found);
      setSelectedCustomer(found.customer || null);
      setSelectedVehicle(found.vehicle || null);
      setLineItems(found.line_items || []);
      form.setFieldsValue({
        customer_notes: found.customer_notes, internal_notes: found.internal_notes,
        valid_until: found.valid_until ? dayjs(found.valid_until) : null,
        customer_id: found.customer_id, vehicle_id: found.vehicle_id,
      });
    } catch (error) {
      console.error('Error fetching estimate:', error);
      message.error('Failed to fetch estimate details');
      navigate('/admin/estimates');
    } finally {
      setLoading(false);
    }
  };

  // ── Customer/Vehicle handlers ──
  const handleCustomerChange = useCallback((customer: Customer) => {
    setSelectedCustomer(customer);
    form.setFieldValue('customer_id', customer.id);
    setHasUnsavedChanges(true);
    formContext.markFieldDirty('customer.first_name');
    formContext.markFieldDirty('customer.last_name');
    if (selectedVehicle && selectedVehicle.customer_id !== customer.id) {
      setSelectedVehicle(null);
      form.setFieldValue('vehicle_id', undefined);
    }
    formContext.onCustomerSaved(customer);
  }, [formContext, form, selectedVehicle]);

  const handleVehicleChange = useCallback((vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    form.setFieldValue('vehicle_id', vehicle.id);
    setHasUnsavedChanges(true);
    formContext.markFieldDirty('vehicle.year');
    formContext.markFieldDirty('vehicle.make');
    formContext.markFieldDirty('vehicle.model');
  }, [form, formContext]);

  const handleClearCustomer = useCallback(() => {
    setSelectedCustomer(null); form.setFieldValue('customer_id', undefined);
    setSelectedVehicle(null); form.setFieldValue('vehicle_id', undefined);
  }, [form]);

  const handleClearVehicle = useCallback(() => {
    setSelectedVehicle(null); form.setFieldValue('vehicle_id', undefined);
  }, [form]);

  const handleOpenEditCustomer = useCallback(() => {
    if (selectedCustomer) { editCustomerForm.setFieldsValue(selectedCustomer); setEditCustomerModalOpen(true); }
  }, [selectedCustomer, editCustomerForm]);

  const handleSaveEditCustomer = async () => {
    try {
      const values = await editCustomerForm.validateFields();
      await customerApi.update(selectedCustomer!.id!, values);
      const response = await customerApi.getById(selectedCustomer!.id!);
      setSelectedCustomer(response.data);
      setEditCustomerModalOpen(false);
      message.success('Customer updated');
    } catch { message.error('Failed to update customer'); }
  };

  const handleEditVehicleSuccess = async () => {
    if (selectedVehicle?.id) {
      const response = await vehicleApi.getById(selectedVehicle.id);
      setSelectedVehicle(response.data);
    }
    setEditVehicleModalOpen(false);
    message.success('Vehicle updated');
  };

  const handleLineItemsChange = useCallback((items: EstimateLineItem[]) => {
    setLineItems(items); setHasUnsavedChanges(true);
  }, []);

  const handleTaxRateChange = (value: string) => {
    setTaxRateInput(value); setTaxRate(parseFloat(value) || 0); setHasUnsavedChanges(true);
  };

  // ── Save ──
  const generateEstimateNumber = () => `CNF-EST-${new Date().getFullYear().toString().slice(-2)}-${String(Date.now()).slice(-5)}`;
  const getTwoWeeksFromNow = () => new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
  const calculateTotals = () => {
    const totals = calculateEstimateTotals(lineItems, debouncedTaxRate);
    return { subtotal: totals.discountedSubtotal, taxAmount: totals.taxAmount, total: totals.total };
  };
  const getDefaultDisclaimer = () => "ESTIMATE DISCLAIMER: This estimate is provided for informational purposes only and is subject to change without notice. Final pricing may vary due to unforeseen circumstances including but not limited to: fluctuations in parts pricing, discovery of additional repairs required, complications arising from previous improper repairs, modifications to the vehicle, rusted, broken, or seized components, and other conditions not apparent during initial inspection. Any work performed outside the scope or price range of this estimate will require customer approval before proceeding. In the event that increased labor costs are not approved, alternative options will be provided to restore the vehicle to its pre-service condition. By accepting this estimate, the customer acknowledges and agrees to these terms and conditions. This estimate is valid for the period specified and may be withdrawn or revised thereafter.";

  const handleSave = async (status: 'draft' | 'sent' = 'draft') => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const { subtotal, taxAmount, total } = calculateTotals();
      const estimateData: any = {
        customer_id: selectedCustomer?.id || null, vehicle_id: selectedVehicle?.id || null,
        customer_notes: values.customer_notes, internal_notes: values.internal_notes,
        line_items: lineItems, parts: [],
        status: (!selectedCustomer || !selectedVehicle) ? 'draft' : status,
        subtotal, tax_rate: taxRate, tax_amount: taxAmount, total_amount: total,
        valid_until: values.valid_until?.toISOString() || getTwoWeeksFromNow(),
        disclaimer: getDefaultDisclaimer(),
      };
      if (isEditing) {
        estimateData.estimate_number = estimate?.estimate_number;
        await estimateApi.update(estimate?.id!, estimateData);
        message.success(`Estimate ${status === 'draft' ? 'saved' : 'sent'} successfully`);
      } else if (draftEstimateId) {
        estimateData.estimate_number = generateEstimateNumber();
        await estimateApi.update(draftEstimateId, estimateData);
        message.success(`Estimate ${status === 'draft' ? 'saved' : 'sent'} successfully`);
      } else {
        estimateData.estimate_number = generateEstimateNumber();
        await estimateApi.create(estimateData);
        message.success(`Estimate ${status === 'draft' ? 'created' : 'created and sent'} successfully`);
      }
      logEvent({ event_id: newEventId(), event_type: 'estimate.saved', entity_type: 'estimate', entity_id: estimate?.id, source: 'manual' });
      setHasUnsavedChanges(false);
      navigate('/admin/estimates');
    } catch (error: any) {
      console.error('Error saving estimate:', error);
      message.error(error?.response?.data?.error || 'Failed to save estimate', 5);
    } finally { setLoading(false); }
  };

  const handleAISave = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const values = await form.validateFields().catch(() => ({}));
      const { subtotal, taxAmount, total } = calculateTotals();
      const estimateData: any = {
        customer_id: selectedCustomer?.id || null, vehicle_id: selectedVehicle?.id || null,
        customer_notes: (values as any).customer_notes, internal_notes: (values as any).internal_notes,
        line_items: lineItems, parts: [], status: 'draft',
        subtotal, tax_rate: taxRate, tax_amount: taxAmount, total_amount: total,
        valid_until: (values as any).valid_until?.toISOString() || getTwoWeeksFromNow(),
        disclaimer: getDefaultDisclaimer(),
      };
      if (isEditing) { estimateData.estimate_number = estimate?.estimate_number; await estimateApi.update(estimate?.id!, estimateData); }
      else { estimateData.estimate_number = generateEstimateNumber(); await estimateApi.create(estimateData); }
      logEvent({ event_id: newEventId(), event_type: 'estimate.saved', entity_type: 'estimate', entity_id: estimate?.id, source: 'ai_interactive' });
      setHasUnsavedChanges(false);
    } catch (error: any) {
      throw new Error(error?.response?.data?.error || error?.message || 'Failed to save estimate');
    } finally { setLoading(false); }
  }, [selectedCustomer, selectedVehicle, lineItems, form, taxRate, isEditing, estimate]);

  // ── Auto-save draft ──
  useEffect(() => {
    if (isEditing || lineItems.length === 0) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        setAutoSaveStatus('saving');
        const totals = calculateEstimateTotals(lineItems, taxRate);
        const res = await estimateApi.saveDraft({
          ...(draftEstimateId ? { id: draftEstimateId } : {}),
          customer_id: selectedCustomer?.id ?? null, vehicle_id: selectedVehicle?.id ?? null,
          line_items: lineItems, subtotal: totals.discountedSubtotal,
          tax_rate: taxRate, tax_amount: totals.taxAmount, total_amount: totals.total,
          status: 'draft', valid_until: getTwoWeeksFromNow(),
        });
        if (res.data?.id) setDraftEstimateId(res.data.id);
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus('idle'), 3000);
      } catch { setAutoSaveStatus('idle'); }
    }, 2000);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, selectedCustomer?.id, selectedVehicle?.id, isEditing, taxRate, draftEstimateId]);

  const { subtotal, taxAmount, total } = calculateTotals();

  // ── AI Chat props (shared between mobile/desktop) ──
  const aiChatProps = {
    serviceItems: lineItems, onAnalyze: ai.handleAIExtraction,
    onCustomerAutoSelected: ai.handleCustomerAutoSelected, onCustomerCandidatesFound: ai.handleCustomerCandidatesFound,
    onVehicleAutoSelected: ai.handleVehicleAutoSelected, onVehicleCandidatesFound: ai.handleVehicleCandidatesFound,
    existingCustomerId: selectedCustomer?.id,
    onCustomerFieldChange: ai.handleAICustomerFieldChange, onVehicleFieldChange: ai.handleAIVehicleFieldChange,
    onServiceItemChange: ai.handleAIServiceItemChange, onAddServiceItem: ai.handleAIAddServiceItem,
    onRemoveServiceItem: ai.handleAIRemoveServiceItem, onSave: handleAISave,
    onCustomerSearch: ai.handleAICustomerSearch, onVehicleSearch: ai.handleAIVehicleSearch,
    hasCustomer: !!selectedCustomer, disabled: loading,
  };

  return (
    <>
      <PageRefreshWarning enabled={hasUnsavedChanges && !loading} />
      <AnimatePresence mode="wait">
        {aiAnalysis.phase === 'analyzing' && (
          <motion.div key="ai-analyzing-banner" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8, transition: { duration: 0.2, ease: 'easeOut' } }} transition={{ duration: 0.2 }}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, backgroundColor: '#1677FF',
              color: 'white', padding: '8px 24px', textAlign: 'center', fontSize: '14px', fontWeight: 500,
              pointerEvents: 'none', animation: 'ai-banner-pulse 1.5s ease-in-out infinite' }}>
            AI analyzing notes...
          </motion.div>
        )}
      </AnimatePresence>
      <style>{`@keyframes ai-banner-pulse { 0%, 100% { opacity: 0.75; } 50% { opacity: 1; } }`}</style>

      <div style={{ display: 'flex', height: isMobile ? 'calc(100vh - 52px)' : '100vh', width: '100%', backgroundColor: '#ffffff' }}>
        {/* Desktop sidebar */}
        {!isMobile && (
          <div style={{
            position: 'relative', width: sidebarCollapsed ? '60px' : '420px',
            minWidth: sidebarCollapsed ? '60px' : '420px', height: '100%',
            backgroundColor: '#fafafa', borderRight: '1px solid #e5e7eb',
            padding: sidebarCollapsed ? '12px 8px' : '24px 20px',
            transition: 'all 0.3s ease', overflowY: 'auto',
          }}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {!sidebarCollapsed && <Text strong style={{ fontSize: '16px', color: '#374151' }}>Customer & Vehicle</Text>}
              <Button type="text" size="small"
                icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)} style={{ color: '#6b7280' }} />
            </div>
            {!sidebarCollapsed && (
              <>
                <UnifiedAIChat {...aiChatProps} />
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UserOutlined style={{ color: '#3b82f6' }} />
                    <Text strong style={{ fontSize: '14px' }}>Customer</Text>
                  </div>
                  {selectedCustomer ? (
                    <div style={{ padding: '8px 12px', background: '#f0f7ff', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span><strong>{selectedCustomer.first_name} {selectedCustomer.last_name}</strong> — {selectedCustomer.phone || selectedCustomer.email}</span>
                      <span>
                        <Button type="link" size="small" icon={<EditOutlined />} onClick={handleOpenEditCustomer}>Edit</Button>
                        <Button type="link" size="small" onClick={handleClearCustomer}>Change</Button>
                      </span>
                    </div>
                  ) : (
                    <CustomerInlineForm value={selectedCustomer || undefined} onChange={handleCustomerChange}
                      aiCustomerCandidates={ai.customerCandidates} onAcceptCandidate={ai.handleAcceptCustomerCandidate}
                      onDismissCandidates={ai.handleDismissCustomerCandidates} />
                  )}
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CarOutlined style={{ color: '#f59e0b' }} />
                    <Text strong style={{ fontSize: '14px' }}>Vehicle</Text>
                  </div>
                  {selectedVehicle ? (
                    <div style={{ padding: '8px 12px', background: '#fffbeb', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span><strong>{selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}</strong> — {selectedVehicle.current_mileage?.toLocaleString()} mi{selectedVehicle.license_plate ? ` | ${selectedVehicle.license_plate}` : ''}</span>
                      <span>
                        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setEditVehicleModalOpen(true)}>Edit</Button>
                        <Button type="link" size="small" onClick={handleClearVehicle}>Change</Button>
                      </span>
                    </div>
                  ) : (
                    <VehicleInlineForm customerId={selectedCustomer?.id}
                      customerName={selectedCustomer ? (selectedCustomer.company_name || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`) : undefined}
                      value={selectedVehicle || undefined} onChange={handleVehicleChange}
                      aiVehicleCandidates={ai.vehicleCandidates} onAcceptVehicleCandidate={ai.handleAcceptVehicleCandidate}
                      onDismissVehicleCandidates={ai.handleDismissVehicleCandidates} />
                  )}
                </div>
              </>
            )}
            {sidebarCollapsed && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: '16px' }}><UserOutlined style={{ fontSize: '20px', color: selectedCustomer ? '#10b981' : '#d1d5db' }} /></div>
                <div><CarOutlined style={{ fontSize: '20px', color: selectedVehicle ? '#10b981' : '#d1d5db' }} /></div>
              </div>
            )}
          </div>
        )}

        {/* Main content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}>
          <EstimateHeader
            isMobile={isMobile} isEditing={isEditing}
            selectedCustomer={selectedCustomer} selectedVehicle={selectedVehicle}
            onNavigateBack={() => navigate('/admin/estimates')}
            onCustomerChange={handleCustomerChange} onVehicleChange={handleVehicleChange}
            onClearCustomer={handleClearCustomer} onClearVehicle={handleClearVehicle}
            onEditCustomer={handleOpenEditCustomer} onEditVehicle={() => setEditVehicleModalOpen(true)}
            customerCandidates={ai.customerCandidates} vehicleCandidates={ai.vehicleCandidates}
            onAcceptCustomerCandidate={ai.handleAcceptCustomerCandidate}
            onDismissCustomerCandidates={ai.handleDismissCustomerCandidates}
            onAcceptVehicleCandidate={ai.handleAcceptVehicleCandidate}
            onDismissVehicleCandidates={ai.handleDismissVehicleCandidates}
          />

          <div style={{ flex: 1, padding: isMobile ? '8px' : '24px', backgroundColor: '#f9fafb', minHeight: 0, overflowY: 'auto' }}>
            {isMobile && <div style={{ marginBottom: 12 }}><UnifiedAIChat {...aiChatProps} /></div>}

            {/* Low-confidence banner */}
            <AnimatePresence>
              {(!aiAnalysis.pendingServiceItems || aiAnalysis.pendingServiceItems.length === 0) &&
               (aiAnalysis.rawServiceItemCount ?? 0) > 0 && !aiAnalysis.serviceItemsApproved && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}
                  style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px',
                    border: '1px solid #ffd591', background: 'linear-gradient(135deg, #fff7e6 0%, #fffbe6 100%)',
                    display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <RobotOutlined style={{ color: '#fa8c16', fontSize: '14px', flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', color: '#d46b08' }}>
                    AI detected {aiAnalysis.rawServiceItemCount} possible item{(aiAnalysis.rawServiceItemCount ?? 0) !== 1 ? 's' : ''} but all were below the 60% confidence threshold.
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            <EstimateLineItems estimateId={estimate?.id || draftEstimateId || 0}
              items={lineItems} isMobile={isMobile} isLocked={false} onItemsChange={handleLineItemsChange}
              taxRate={taxRate} vehicle={selectedVehicle || undefined} />
          </div>

          <EstimateFormFields form={form} isMobile={isMobile} loading={loading}
            isFormValid={true} isEditing={isEditing} autoSaveStatus={autoSaveStatus}
            subtotal={subtotal} taxAmount={taxAmount} total={total}
            taxRateInput={taxRateInput} onTaxRateChange={handleTaxRateChange}
            onSave={handleSave} selectedVehicle={selectedVehicle} />
        </div>
      </div>

      {/* Edit Customer Modal */}
      <Modal title="Edit Customer" open={editCustomerModalOpen}
        onCancel={() => setEditCustomerModalOpen(false)} onOk={handleSaveEditCustomer} okText="Save"
        width={window.innerWidth >= 768 ? 600 : '90%'} style={{ margin: '0 auto' }}
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }} centered destroyOnHidden>
        <Form form={editCustomerForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>
            <Input size="large" style={{ fontSize: 16 }} />
          </Form.Item>
          <Form.Item name="last_name" label="Last Name" rules={[{ required: true }]}>
            <Input size="large" style={{ fontSize: 16 }} />
          </Form.Item>
          <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
            <Input size="large" style={{ fontSize: 16 }} />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
            <Input size="large" style={{ fontSize: 16 }} />
          </Form.Item>
          <Form.Item name="company_name" label="Company (Optional)">
            <Input size="large" style={{ fontSize: 16 }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Vehicle Modal */}
      <Modal
        title={selectedVehicle ? `Edit ${selectedVehicle.year} ${selectedVehicle.make} ${selectedVehicle.model}` : 'Edit Vehicle'}
        open={editVehicleModalOpen} onCancel={() => setEditVehicleModalOpen(false)} footer={null}
        width={window.innerWidth >= 768 ? 800 : '90%'} style={{ margin: '0 auto' }}
        styles={{ body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' } }} centered destroyOnHidden>
        {selectedVehicle && (
          <VehicleForm customerId={selectedVehicle.customer_id} propVehicleId={selectedVehicle.id}
            onSuccess={handleEditVehicleSuccess} onCancel={() => setEditVehicleModalOpen(false)} modal />
        )}
      </Modal>
    </>
  );
};

export default EstimateForm;
